import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, MapPin, Search as SearchIcon, Filter, Save } from 'lucide-react';
import { uuidv7 } from 'uuidv7';
import { api, APIError } from '../api/client';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { clearAuthSession, getUserId } from '../utils/auth';
import { pushDataLayer } from '../utils/dataLayer';
import { trackEvent } from '../utils/amplitude';
import { COUNTRIES, DEFAULT_COUNTRY, getCountryCode, getLanguageHint } from '../config/countries';

export default function SearchScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authModal = useAuthModal();
  const [selectedCountries, setSelectedCountries] = useState([DEFAULT_COUNTRY]);
  const [jobKeywords, setJobKeywords] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);

  // Advanced filters state
  const [datePosted, setDatePosted] = useState('all');
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [jobRequirements, setJobRequirements] = useState('');
  const [radius, setRadius] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Manual vacancy upload state
  const [activeSearchTab, setActiveSearchTab] = useState('search');
  const [vacancyText, setVacancyText] = useState('');
  const [vacancySaving, setVacancySaving] = useState(false);
  const [vacancyError, setVacancyError] = useState('');

  const intervalRef = useRef(null);
  const countryMenuRef = useRef(null);


  const handleCountryToggle = (country) => {
    // Теперь всегда выбирается только одна страна
    setSelectedCountries([country]);
    setIsCountryMenuOpen(false); // Закрываем меню после выбора
  };

  const handleEmploymentTypeToggle = (type) => {
    setEmploymentTypes(prev => {
      if (prev.includes(type)) return prev.filter(t => t !== type);
      return [...prev, type];
    });
  };

  // Очищаем интервал при размонтировании компонента
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryMenuRef.current && !countryMenuRef.current.contains(event.target)) {
        setIsCountryMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const q = (searchParams.get('q') || '').trim();
    if (q) setJobKeywords(q);
  }, [searchParams]);

  const checkRequestStatus = async (requestId) => {
    try {
      console.log(`[Polling] Checking status for request_id: ${requestId}`);

      const data = await api.get(`/api/jobs_search_requests/status/${requestId}`);
      console.log(`[Polling] Status response:`, data);

      // Если статус успешный (status === '2' или 2), возвращаем true
      if (data.status === '2' || data.status === 2) {
        console.log(`[Polling] Status is SUCCESS (2), redirecting to results`);
        return true;
      }

      console.log(`[Polling] Status is ${data.status}, continue waiting...`);
      return false;
    } catch (err) {
      if (err instanceof APIError && (err.status === 401 || err.status === 403)) {
        console.log('[Polling] Unauthorized, clearing auth session and showing auth modal');
        clearAuthSession();
        authModal.openAuth('search');
        return 'unauthorized';
      }
      console.error('[Polling] Status check error:', err);
      return false;
    }
  };

  const handleSearch = async () => {
    const normalizedQuery = jobKeywords.trim();

    // Проверка авторизации в первую очередь
    const userId = getUserId();
    const isAuthorized = !!userId;

    pushDataLayer({
      event: 'job_search_click',
      location: 'search',
      country: selectedCountries?.[0] || '',
      user_id: userId ? String(userId) : null,
      filters: {
        is_text: normalizedQuery ? 1 : 0,
        is_date: datePosted !== 'all' ? 1 : 0,
        is_employed_type: employmentTypes.length > 0 ? 1 : 0,
        is_remoute: remoteOnly ? 1 : 0,
        is_experience: jobRequirements.trim() ? 1 : 0,
        is_radius: radius ? 1 : 0,
      },
      data_source: 'front',
    });
    trackEvent('vacancies_search');

    if (!isAuthorized) {
      console.log('[Search] User not authorized, showing auth modal');
      authModal.openAuth('search');
      return;
    }

    if (!normalizedQuery) {
      console.log('[Search] Keywords empty, returning');
      return;
    }

    console.log('[Search] Starting search...');
    setLoading(true);

    try {
      // Генерируем уникальный UUID v7 для этого запроса
      const requestId = uuidv7();
      console.log('[Search] Generated request_id:', requestId);

      // Авторизованный пользователь - отправляем реальные данные
      const selectedCountry = selectedCountries.length > 0 ? selectedCountries[0] : DEFAULT_COUNTRY;
      const countryCode = getCountryCode(selectedCountry);

      const requestBody = {
        query: normalizedQuery,
        country_code: countryCode,
        request_id: requestId,
        // New filters
        date_posted: datePosted,
        employment_types: employmentTypes.join(','),
        remote_jobs_only: remoteOnly,
        job_requirements: jobRequirements,
        radius: radius ? parseInt(radius) : null
      };
      console.log('[Search] Request body:', requestBody);

      // Отправляем POST запрос
      console.log('[Search] Sending POST request...');
      await api.post('/api/jobs_search_requests/search', requestBody);
      console.log('[Search] POST request successful');

      // Запускаем проверку статуса каждые 5 секунд
      console.log('[Search] Starting polling every 5 seconds...');
      const statusCheckInterval = setInterval(async () => {
        const status = await checkRequestStatus(requestId);

        if (status === 'unauthorized') {
          clearInterval(statusCheckInterval);
          setLoading(false);
          return;
        }

        if (status) {
          // Очищаем интервал
          clearInterval(statusCheckInterval);
          setLoading(false);

          // Сохраняем request_id в localStorage для получения результатов
          localStorage.setItem('lastSearchRequestId', requestId);
          console.log('[Search] Saved request_id to localStorage, navigating to results');

          navigate('/results');
        }
      }, 5000); // Проверяем каждые 5 секунд

      // Сохраняем ссылку на интервал для возможности очистки
      intervalRef.current = statusCheckInterval;

    } catch (err) {
      if (err instanceof APIError && (err.status === 401 || err.status === 403)) {
        console.log('[Search] Unauthorized response, clearing auth session and showing auth modal');
        clearAuthSession();
        authModal.openAuth('search');
        setLoading(false);
        return;
      }
      console.error('[Search] Search error:', err);
      setLoading(false);
      // При ошибке не перенаправляем пользователя
    }
  };

  const handleUploadVacancy = async () => {
    if (!getUserId()) {
      authModal.openAuth('search');
      return;
    }

    const text = vacancyText.trim();
    if (!text) {
      setVacancyError('Vacancy description cannot be empty.');
      return;
    }

    setVacancySaving(true);
    setVacancyError('');

    try {
      const data = await api.post('/api/vacancies/manual', { text });

      if (!data.vacancy_id) {
        setVacancyError('Upload succeeded but vacancy_id is missing in response.');
        return;
      }

      navigate(`/vacancy/${data.vacancy_id}`);
    } catch (err) {
      if (err instanceof APIError) {
        if (err.status === 401 || err.status === 403) {
          clearAuthSession();
          authModal.openAuth('search');
          return;
        }
        setVacancyError(err.data?.detail || err.data?.message || 'Failed to upload vacancy');
        return;
      }
      console.error('[Vacancy] Upload error:', err);
      setVacancyError('An error occurred while uploading the vacancy.');
    } finally {
      setVacancySaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B]">
      <SiteHeader authModal={authModal} />

      <main id="main-content" className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-12 space-y-6 sm:space-y-10">
        <section className="flex justify-center">
          <div role="tablist" aria-label="Search methods" className="inline-flex items-center rounded-full border border-gray-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              role="tab"
              id="search-vacancies-tab"
              aria-selected={activeSearchTab === 'search'}
              aria-controls="search-vacancies-panel"
              onClick={() => setActiveSearchTab('search')}
              className={`rounded-full px-6 py-2 text-sm font-semibold transition ${activeSearchTab === 'search' ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Search vacancies
            </button>
            <button
              type="button"
              role="tab"
              id="upload-text-tab"
              aria-selected={activeSearchTab === 'upload'}
              aria-controls="upload-text-panel"
              onClick={() => {
                setActiveSearchTab('upload');
                setVacancyError('');
                const userId = getUserId();
                pushDataLayer({
                  event: 'ui_interaction',
                  action: 'click',
                  element: 'upload_descriptions',
                  location: 'search',
                  user_id: userId ? String(userId) : null,
                  data_source: 'front',
                });
              }}
              className={`rounded-full px-6 py-2 text-sm font-semibold transition ${activeSearchTab === 'upload' ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Upload by text
            </button>
          </div>
        </section>

        <section className="md:hidden">
          <div className="rounded-2xl bg-white px-5 py-6 text-center">
            <h1 className="text-3xl font-semibold">Start AI Interview</h1>
            <p className="mt-2 text-sm text-gray-500 leading-relaxed">
              Find a vacancy online or paste a job description — our AI will generate a tailored mock interview for you
            </p>
          </div>

          <div className="-mt-2 rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
            {activeSearchTab === 'search' ? (
              <div
                role="tabpanel"
                id="search-vacancies-panel"
                aria-labelledby="search-vacancies-tab"
                className="space-y-4"
              >
                <div>
                  <label htmlFor="mobile-job-keywords-input" className="mb-1.5 block text-sm font-semibold text-slate-900">
                    Role or skills{' '}
                    <span className="text-xs font-medium text-orange-600">
                      ({getLanguageHint(selectedCountries[0])})
                    </span>
                  </label>
                  <div className="flex items-center gap-2 rounded-xl border border-gray-200 px-3">
                    <SearchIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    <input
                      id="mobile-job-keywords-input"
                      type="text"
                      placeholder="e.g. Product Analyst"
                      className="w-full bg-transparent py-3 text-sm outline-none"
                      value={jobKeywords}
                      onChange={(e) => setJobKeywords(e.target.value)}
                      aria-required="true"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="mobile-country-select" className="mb-1.5 block text-sm font-semibold text-slate-900">Country</label>
                  <div className="relative">
                    <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                    <select
                      id="mobile-country-select"
                      value={selectedCountries[0] || DEFAULT_COUNTRY}
                      onChange={(e) => handleCountryToggle(e.target.value)}
                      className="w-full appearance-none rounded-xl border border-gray-200 bg-white py-3 pl-9 pr-9 text-sm text-gray-700 outline-none"
                      aria-label="Select country for job search"
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.label} value={c.label}>{c.flag} {c.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading}
                  aria-busy={loading}
                  className="btn-orange w-full py-3 text-sm font-semibold disabled:opacity-50"
                >
                  {loading ? 'Searching...' : 'Search Vacancies'}
                </button>

                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  aria-expanded={showFilters}
                  aria-controls="mobile-advanced-filters"
                  className="mx-auto flex items-center gap-2 text-sm text-gray-500"
                >
                  <Filter className="h-4 w-4" aria-hidden="true" />
                  {showFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
                  <ChevronDown className={`h-4 w-4 transition ${showFilters ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
              </div>
            ) : (
              <div
                role="tabpanel"
                id="upload-text-panel"
                aria-labelledby="upload-text-tab"
                className="space-y-4"
              >
                {vacancyError && (
                  <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    <span className="sr-only">Error:</span> {vacancyError}
                  </div>
                )}
                <label htmlFor="mobile-vacancy-textarea" className="sr-only">Vacancy Description</label>
                <textarea
                  id="mobile-vacancy-textarea"
                  rows="8"
                  className="custom-input resize-none w-full"
                  value={vacancyText}
                  onChange={(e) => {
                    setVacancyText(e.target.value);
                    if (vacancyError) setVacancyError('');
                  }}
                  disabled={vacancySaving}
                  aria-label="Paste full vacancy description"
                  aria-invalid={!!vacancyError}
                  aria-busy={vacancySaving}
                  placeholder="Paste the vacancy description here..."
                />
                <button
                  type="button"
                  onClick={() => {
                    const userId = getUserId();
                    pushDataLayer({
                      event: 'ui_interaction',
                      action: 'click',
                      element: 'done_button',
                      location: 'search',
                      user_id: userId ? String(userId) : null,
                      data_source: 'front',
                    });
                    handleUploadVacancy();
                  }}
                  disabled={vacancySaving || !vacancyText.trim()}
                  aria-busy={vacancySaving}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  <Save className="h-4 w-4" aria-hidden="true" />
                  {vacancySaving ? 'Saving...' : 'Done'}
                </button>
              </div>
            )}

            {activeSearchTab === 'search' && showFilters && (
              <div id="mobile-advanced-filters" className="mt-5 border-t border-gray-100 pt-4 space-y-4 animate-fadeIn">
                <fieldset>
                  <legend className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Date posted</legend>
                  <div role="group" aria-label="Date posted filter" className="flex flex-wrap gap-2">
                    {[
                      { value: 'all', label: 'Any time' },
                      { value: 'today', label: 'Today' },
                      { value: '3days', label: '3 days' },
                      { value: 'week', label: 'Last week' },
                      { value: 'month', label: 'Last month' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={datePosted === option.value}
                        onClick={() => setDatePosted(option.value)}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${datePosted === option.value ? 'border-orange-500 bg-orange-50 text-orange-600' : 'border-gray-200 text-gray-600'}`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <fieldset>
                  <legend className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Employment type</legend>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {[
                      { value: 'FULLTIME', label: 'Full-time' },
                      { value: 'CONTRACTOR', label: 'Contractor' },
                      { value: 'PARTTIME', label: 'Part-time' },
                      { value: 'INTERN', label: 'Intern' },
                    ].map((type) => (
                      <label key={type.value} htmlFor={`mobile-employment-${type.value}`} className="flex items-center gap-2 rounded-lg border border-gray-200 px-2.5 py-2 cursor-pointer">
                        <input
                          id={`mobile-employment-${type.value}`}
                          type="checkbox"
                          checked={employmentTypes.includes(type.value)}
                          onChange={() => handleEmploymentTypeToggle(type.value)}
                          className="rounded"
                        />
                        <span>{type.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div>
                  <label htmlFor="mobile-experience-select" className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">Experience</label>
                  <select
                    id="mobile-experience-select"
                    value={jobRequirements}
                    onChange={(e) => setJobRequirements(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none"
                  >
                    <option value="">Any experience</option>
                    <option value="under_3_years_experience">Under 3 years</option>
                    <option value="more_than_3_years_experience">More than 3 years</option>
                    <option value="no_experience">No experience</option>
                    <option value="no_degree">No degree</option>
                  </select>
                </div>

                <label htmlFor="mobile-remote-checkbox" className="flex items-center gap-2 rounded-xl border border-dashed border-gray-300 px-3 py-2.5 text-sm cursor-pointer">
                  <input
                    id="mobile-remote-checkbox"
                    type="checkbox"
                    checked={remoteOnly}
                    onChange={(e) => setRemoteOnly(e.target.checked)}
                    className="rounded"
                  />
                  Remote Work Only
                </label>

                <div>
                  <label htmlFor="mobile-radius-range" className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">Radius</label>
                  <div className="flex items-center gap-3">
                    <input
                      id="mobile-radius-range"
                      type="range"
                      min="0"
                      max="100"
                      step="10"
                      value={radius || 0}
                      onChange={(e) => setRadius(e.target.value)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={radius || 0}
                      aria-label="Search radius in kilometers"
                      className="flex-1"
                    />
                    <span aria-live="polite" className="w-14 text-right text-sm text-gray-600">
                      {radius ? `${radius}km` : 'Auto'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {activeSearchTab === 'search' && (
            <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-slate-700">
              <strong>Quick tip:</strong> use specific tools and levels in keywords, for example:
              {' '}Senior Product Analyst SQL Python.
            </div>
          )}
        </section>

        <section className="hidden md:block space-y-6">
          <section className="px-2 py-2 text-center">
            <h1 className="text-[32px] leading-tight font-semibold text-slate-900">Start AI Interview</h1>
            <p className="text-sm text-gray-500 mt-2 max-w-2xl mx-auto leading-relaxed">
              Find a vacancy online or paste a job description — our AI will generate a tailored mock interview for you
            </p>
          </section>

          <section className="bg-white rounded-[28px] border border-gray-200 shadow-sm p-6 md:p-8">
          {activeSearchTab === 'search' ? (
          <div
            role="tabpanel"
            id="desktop-search-panel"
            aria-labelledby="search-vacancies-tab"
          >
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-xs font-bold text-white" aria-hidden="true">1</div>
            <span className="text-xs font-bold uppercase tracking-[0.08em] text-slate-700">Search Filters</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2">
              <label htmlFor="desktop-job-keywords-input" className="block text-sm font-semibold text-gray-700 mb-2">
                Role or skills{' '}
                <span className="text-orange-500 font-normal">
                  ({getLanguageHint(selectedCountries[0])})
                </span>
              </label>
              <div className="flex items-center gap-3 border border-gray-200 rounded-2xl px-4 py-3">
                <SearchIcon className="w-4 h-4 text-gray-400" aria-hidden="true" />
                <input
                  id="desktop-job-keywords-input"
                  type="text"
                  placeholder="e.g. Product Analyst"
                  className="flex-1 text-sm outline-none bg-transparent"
                  value={jobKeywords}
                  onChange={(e) => setJobKeywords(e.target.value)}
                  aria-required="true"
                />
              </div>
            </div>

            <div className="lg:col-span-2 relative" ref={countryMenuRef}>
              <label id="desktop-country-label" className="block text-sm font-semibold text-gray-700 mb-2">
                Country
              </label>
              <button
                type="button"
                onClick={() => setIsCountryMenuOpen((prev) => !prev)}
                aria-expanded={isCountryMenuOpen}
                aria-controls="desktop-country-menu"
                aria-labelledby="desktop-country-label"
                className="w-full flex items-center justify-between gap-3 border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-600 hover:border-gray-300"
              >
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" aria-hidden="true" />
                  {selectedCountries.length > 0 ? selectedCountries.join(', ') : 'Select country'}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden="true" />
              </button>

              {isCountryMenuOpen && (
                <div
                  id="desktop-country-menu"
                  role="menu"
                  aria-labelledby="desktop-country-label"
                  className="absolute top-full left-0 z-50 mt-2 w-full rounded-2xl border border-gray-200 bg-white shadow-lg p-4 flex flex-wrap gap-2"
                >
                  {COUNTRIES.map((c) => (
                    <button
                      key={c.label}
                      type="button"
                      role="menuitemradio"
                      aria-checked={selectedCountries.includes(c.label)}
                      onClick={() => handleCountryToggle(c.label)}
                      className={`px-4 py-2 rounded-2xl text-sm font-medium transition ${selectedCountries.includes(c.label)
                        ? 'border-2 border-blue-500 bg-blue-50 text-blue-500'
                        : 'border border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                        }`}
                    >
                      {c.flag} {c.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-1 flex items-end">
              <button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                aria-busy={loading}
                className="btn-orange w-full text-sm py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Searching...' : 'Search Vacancies'}
              </button>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-xs font-bold text-white" aria-hidden="true">2</div>
              <span className="text-xs font-bold uppercase tracking-[0.08em] text-slate-700">Advanced Filters</span>
            </div>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              aria-expanded={showFilters}
              aria-controls="desktop-advanced-filters"
              className="flex items-center gap-2 text-sm font-semibold text-orange-600 mb-4 hover:underline"
            >
              <Filter className="w-4 h-4" aria-hidden="true" />
              {showFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
            </button>

            {showFilters && (
              <div id="desktop-advanced-filters" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-fadeIn">
                {/* Date Posted */}
                <div className="space-y-2">
                  <label htmlFor="desktop-date-posted-select" className="block text-xs font-semibold text-gray-500 uppercase">Date Posted</label>
                  <select
                    id="desktop-date-posted-select"
                    value={datePosted}
                    onChange={(e) => setDatePosted(e.target.value)}
                    className="w-full p-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500"
                  >
                    <option value="all">Any time</option>
                    <option value="today">Today</option>
                    <option value="3days">Last 3 days</option>
                    <option value="week">Last week</option>
                    <option value="month">Last month</option>
                  </select>
                </div>

                {/* Employment Type */}
                <fieldset className="space-y-2">
                  <legend className="text-xs font-semibold text-gray-500 uppercase">Employment Type</legend>
                  <div className="flex flex-col gap-2">
                    {[
                      { value: 'FULLTIME', label: 'Full-time' },
                      { value: 'CONTRACTOR', label: 'Contractor' },
                      { value: 'PARTTIME', label: 'Part-time' },
                      { value: 'INTERN', label: 'Intern' }
                    ].map(type => (
                      <label key={type.value} htmlFor={`desktop-employment-${type.value}`} className="flex items-center gap-2 cursor-pointer">
                        <input
                          id={`desktop-employment-${type.value}`}
                          type="checkbox"
                          checked={employmentTypes.includes(type.value)}
                          onChange={() => handleEmploymentTypeToggle(type.value)}
                          className="rounded text-blue-500 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                {/* Requirements & Remote */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="desktop-experience-select" className="block text-xs font-semibold text-gray-500 uppercase">Experience</label>
                    <select
                      id="desktop-experience-select"
                      value={jobRequirements}
                      onChange={(e) => setJobRequirements(e.target.value)}
                      className="w-full p-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500"
                    >
                      <option value="">Any experience</option>
                      <option value="under_3_years_experience">Under 3 years</option>
                      <option value="more_than_3_years_experience">More than 3 years</option>
                      <option value="no_experience">No experience</option>
                      <option value="no_degree">No degree</option>
                    </select>
                  </div>

                  <label htmlFor="desktop-remote-checkbox" className="flex items-center gap-2 cursor-pointer p-2 border border-dashed border-gray-300 rounded-xl hover:bg-gray-50">
                    <input
                      id="desktop-remote-checkbox"
                      type="checkbox"
                      checked={remoteOnly}
                      onChange={(e) => setRemoteOnly(e.target.checked)}
                      className="rounded text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Remote Work Only</span>
                  </label>
                </div>

                {/* Radius */}
                <div className="space-y-2">
                  <label htmlFor="desktop-radius-range" className="block text-xs font-semibold text-gray-500 uppercase">Radius (km)</label>
                  <div className="flex items-center gap-3">
                    <input
                      id="desktop-radius-range"
                      type="range"
                      min="0"
                      max="100"
                      step="10"
                      value={radius || 0}
                      onChange={(e) => setRadius(e.target.value)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={radius || 0}
                      aria-label="Search radius in kilometers"
                      className="flex-1"
                    />
                    <span aria-live="polite" className="text-sm font-mono text-gray-600 min-w-[3ch]">
                      {radius ? `${radius}km` : 'Auto'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">Distance from selected location</p>
                </div>
              </div>
            )}
          </div>
          </div>
          ) : (
            <div
              role="tabpanel"
              id="desktop-upload-panel"
              aria-labelledby="upload-text-tab"
              className="space-y-4"
            >
              {vacancyError && (
                <div role="alert" className="mb-1 p-3 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-sm">
                  <span className="sr-only">Error:</span> {vacancyError}
                </div>
              )}
              <label htmlFor="desktop-vacancy-textarea" className="sr-only">Vacancy Description</label>
              <textarea
                id="desktop-vacancy-textarea"
                rows="10"
                className="custom-input resize-none w-full"
                value={vacancyText}
                onChange={(e) => {
                  setVacancyText(e.target.value);
                  if (vacancyError) setVacancyError('');
                }}
                disabled={vacancySaving}
                aria-label="Paste full vacancy description"
                aria-invalid={!!vacancyError}
                aria-busy={vacancySaving}
                placeholder="Paste the vacancy description here..."
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    const userId = getUserId();
                    pushDataLayer({
                      event: 'ui_interaction',
                      action: 'click',
                      element: 'done_button',
                      location: 'search',
                      user_id: userId ? String(userId) : null,
                      data_source: 'front',
                    });
                    handleUploadVacancy();
                  }}
                  disabled={vacancySaving || !vacancyText.trim()}
                  aria-busy={vacancySaving}
                  className="flex items-center justify-center gap-2 bg-orange-500 text-white rounded-2xl px-6 py-3 text-sm font-semibold hover:bg-orange-600 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" aria-hidden="true" />
                  {vacancySaving ? 'Saving...' : 'Done'}
                </button>
              </div>
            </div>
          )}
          </section>
        </section>
      </main>

      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
}
